import type { FormField } from '@/types/form';

import { GridField } from './fields/GridField/GridField';
import { TimeField } from './fields/TimeField/TimeField';
import { FileUploadField } from './fields/FileUploadField/FileUploadField';

import styles from './FormFieldRenderer.module.css';

interface FormFieldRendererProps {
    field: FormField;
    value: string;
    setValue: (value: string) => void;
    error?: string;
    onFileUpload?: (file: File) => Promise<string>;
}

const OPTION_COLORS = [
    'var(--option-color-1)',
    'var(--option-color-2)',
    'var(--option-color-3)',
    'var(--option-color-4)',
    'var(--option-color-5)',
    'var(--option-color-6)',
];

function getChoices(field: FormField): string[] {
    const raw = field.config['choices'];
    if (Array.isArray(raw)) return raw as string[];
    return [];
}

function getScaleRange(field: FormField): { min: number; max: number; minLabel?: string; maxLabel?: string } {
    const min = typeof field.config['min'] === 'number' ? field.config['min'] : 1;
    const max = typeof field.config['max'] === 'number' ? field.config['max'] : 5;
    const minLabel = typeof field.config['min_label'] === 'string' ? field.config['min_label'] : undefined;
    const maxLabel = typeof field.config['max_label'] === 'string' ? field.config['max_label'] : undefined;
    return { min, max, minLabel, maxLabel };
}

export function FormFieldRenderer({ field, value, setValue, error, onFileUpload }: FormFieldRendererProps) {
    const renderInput = () => {
        switch (field.field_type) {
            case 'short_text':
                return (
                    <input
                        type="text"
                        className={`${styles.textInput} ${error ? styles.inputError : ''}`}
                        value={value}
                        onChange={(e) => setValue(e.target.value)}
                        placeholder="Tu respuesta"
                        maxLength={
                            typeof field.config['max_length'] === 'number'
                                ? field.config['max_length']
                                : 200
                        }
                    />
                );

            case 'long_text':
                return (
                    <textarea
                        className={`${styles.textarea} ${error ? styles.inputError : ''}`}
                        value={value}
                        onChange={(e) => setValue(e.target.value)}
                        placeholder="Tu respuesta"
                        rows={4}
                        maxLength={
                            typeof field.config['max_length'] === 'number'
                                ? field.config['max_length']
                                : 2000
                        }
                    />
                );

            case 'single_choice': {
                const choices = getChoices(field);
                return (
                    <div className={styles.choicesGroup}>
                        {choices.map((choice, i) => (
                            <label
                                key={choice}
                                className={`${styles.choiceItem} ${value === choice ? styles.choiceSelected : ''}`}
                                style={{ '--accent': OPTION_COLORS[i % OPTION_COLORS.length] } as React.CSSProperties}
                            >
                                <input
                                    type="radio"
                                    className={styles.hiddenInput}
                                    name={`field-${field.id}`}
                                    value={choice}
                                    checked={value === choice}
                                    onChange={() => setValue(choice)}
                                />
                                <span className={styles.choiceAccent} />
                                <span className={styles.choiceText}>{choice}</span>
                            </label>
                        ))}
                    </div>
                );
            }

            case 'multiple_choice': {
                const choices = getChoices(field);
                const selected = value ? value.split('||') : [];
                return (
                    <div className={styles.choicesGroup}>
                        {choices.map((choice, i) => {
                            const checked = selected.includes(choice);
                            return (
                                <label
                                    key={choice}
                                    className={`${styles.choiceItem} ${checked ? styles.choiceSelected : ''}`}
                                    style={{ '--accent': OPTION_COLORS[i % OPTION_COLORS.length] } as React.CSSProperties}
                                >
                                    <input
                                        type="checkbox"
                                        className={styles.hiddenInput}
                                        checked={checked}
                                        onChange={() => {
                                            const next = checked
                                                ? selected.filter((s) => s !== choice)
                                                : [...selected, choice];
                                            setValue(next.join('||'));
                                        }}
                                    />
                                    <span className={styles.choiceAccent} />
                                    <span className={styles.choiceText}>{choice}</span>
                                </label>
                            );
                        })}
                    </div>
                );
            }

            case 'dropdown': {
                const choices = getChoices(field);
                return (
                    <select
                        className={`${styles.select} ${error ? styles.inputError : ''}`}
                        value={value}
                        onChange={(e) => setValue(e.target.value)}
                    >
                        <option value="">Selecciona una opcion...</option>
                        {choices.map((choice) => (
                            <option key={choice} value={choice}>{choice}</option>
                        ))}
                    </select>
                );
            }

            case 'linear_scale': {
                const { min, max, minLabel, maxLabel } = getScaleRange(field);
                const numbers = Array.from({ length: max - min + 1 }, (_, i) => min + i);
                return (
                    <div className={styles.scaleWrapper}>
                        <div className={styles.scaleButtons}>
                            {numbers.map((n) => (
                                <button
                                    key={n}
                                    type="button"
                                    className={`${styles.scaleBtn} ${value === String(n) ? styles.scaleBtnActive : ''}`}
                                    onClick={() => setValue(String(n))}
                                >
                                    {n}
                                </button>
                            ))}
                        </div>
                        {(minLabel || maxLabel) && (
                            <div className={styles.scaleLabels}>
                                <span className={styles.scaleLabel}>{minLabel}</span>
                                <span className={styles.scaleLabel}>{maxLabel}</span>
                            </div>
                        )}
                    </div>
                );
            }

            case 'date':
                return (
                    <input
                        type="date"
                        className={`${styles.textInput} ${error ? styles.inputError : ''}`}
                        value={value}
                        onChange={(e) => setValue(e.target.value)}
                    />
                );

            case 'number': {
                const numMin = typeof field.config['min'] === 'number' ? field.config['min'] : undefined;
                const numMax = typeof field.config['max'] === 'number' ? field.config['max'] : undefined;
                return (
                    <input
                        type="number"
                        className={`${styles.textInput} ${error ? styles.inputError : ''}`}
                        value={value}
                        onChange={(e) => setValue(e.target.value)}
                        min={numMin}
                        max={numMax}
                        placeholder="0"
                    />
                );
            }

            case 'time':
                return (
                    <TimeField value={value} setValue={setValue} error={error} />
                );

            case 'file_upload': {
                const maxSizeMb = typeof field.config['max_size_mb'] === 'number' ? field.config['max_size_mb'] : 10;
                const allowedTypes = Array.isArray(field.config['allowed_types'])
                    ? (field.config['allowed_types'] as string[])
                    : [];
                return (
                    <FileUploadField
                        value={value}
                        setValue={setValue}
                        error={error}
                        maxSizeMb={maxSizeMb}
                        allowedTypes={allowedTypes}
                        onFileSelected={onFileUpload}
                    />
                );
            }

            case 'choice_grid':
            case 'checkbox_grid':
                return (
                    <GridField field={field} value={value} setValue={setValue} error={error} />
                );

            default:
                return null;
        }
    };

    return (
        <div className={styles.fieldWrapper}>
            <label className={styles.label}>
                {field.label}
                {field.is_required && <span className={styles.required} aria-hidden="true"> *</span>}
            </label>
            {field.description && (
                <p className={styles.description}>{field.description}</p>
            )}
            {renderInput()}
            {error && field.field_type !== 'time' && field.field_type !== 'file_upload' && field.field_type !== 'choice_grid' && field.field_type !== 'checkbox_grid' && (
                <span className={styles.errorMsg} role="alert">{error}</span>
            )}
        </div>
    );
}
